// NEW: UI_REVAMP
import { Box, Flex, Text } from '@chakra-ui/react';
import useIsMobileView from '../../hooks/useIsMobileView';
import { RECORD_GRADIENTS, RECORD_SHADOWS, RECORD_TEXT_COLORS } from '../../styles/recordUiTokens';
import ImgComp from '../../utils/ImgComp';
import { currencyFormat } from '../../utils/numberFormat';
import CopyValueButton from '../common/CopyValueButton';
import GradientText from '../common/GradientText';
import { getRecordStatusStyle } from './historyStatusStyles';

const HistoryList = ({
	status,
	timeData,
	drawDT,
	ticketId,
	gameType,
	betTotal,
	betCount,
	amountTtl,
	isExpire,
	ticket,
	serverTime,
	onClick,
}) => {
	const statusStyles = getRecordStatusStyle(status, {
		isExpire,
		variant: 'history',
		ticket,
		serverTime,
	});
	const cardTitle = getHistoryCardTitle(statusStyles.label, betCount);
	const isMobileView = useIsMobileView();
	const setFontSize = isMobileView ? '14px' : '16px';
	const formattedWinningAmount =
		Math.trunc(Number(amountTtl) || 0) === 0
			? '-'
			: currencyFormat(Math.trunc(Number(amountTtl) || 0));

	return (
		<Box
			width='100%'
			marginY={3}
			cursor='pointer'
			boxSizing='border-box'
			padding='1px'
			borderRadius='8px'
			background={RECORD_GRADIENTS.goldBorder}
			boxShadow={RECORD_SHADOWS.goldCard}
			onClick={onClick}
		>
			<Box padding='8px' borderRadius='7px' background={RECORD_GRADIENTS.historyCardSurface}>
				<Flex
					justifyContent='space-between'
					alignItems='flex-start'
					marginBottom='10px'
					gap='12px'
					padding={'4px'}
				>
					<Text
						fontSize='16px'
						fontWeight='500'
						lineHeight='100%'
						color={RECORD_TEXT_COLORS.secondary}
					>
						{cardTitle}
					</Text>
					<Box width='14px' height='14px' color={RECORD_TEXT_COLORS.primary} flexShrink={0}>
						<ImgComp svgName='chevron-right' width='14px' height='14px' />
					</Box>
				</Flex>

				<Box>
					<HistoryRow
						title='Ticket ID'
						data={ticketId}
						color={RECORD_TEXT_COLORS.link}
						valueFontWeight='600'
						valueFontSize={setFontSize}
						valueIcon={
							<CopyValueButton
								value={ticketId}
								successMessage='Copied Ticket ID'
								ariaLabel='Copy Ticket ID'
								color={RECORD_TEXT_COLORS.link}
							/>
						}
					/>
					<HistoryRow
						title='Bet Date Time'
						data={timeData}
						valueFontWeight='600'
						valueFontSize={setFontSize}
					/>
					<HistoryRow
						title='Draw Date Time'
						data={drawDT || '-'}
						valueFontWeight='600'
						valueFontSize={setFontSize}
					/>
					<HistoryRow
						title='Site Type'
						data={gameType}
						valueFontWeight='600'
						valueFontSize={setFontSize}
						hideOnMobile
					/>
					<HistoryRow
						title='Total Bet Amount (PHP)'
						data={betTotal}
						isGradientText
						valueFontWeight='700'
						valueFontSize={setFontSize}
						hideOnMobile
						isMoney
					/>
					<HistoryRow
						title='Total Win Amount (PHP)'
						data={formattedWinningAmount}
						isGradientText={formattedWinningAmount !== '-'}
						color={formattedWinningAmount === '-' ? RECORD_TEXT_COLORS.primary : undefined}
						valueFontWeight='700'
						valueFontSize={setFontSize}
						hideOnMobile
						isMoney
					/>
					<HistoryRow
						title='Ticket Status'
						data={
							<HistoryStatusPill
								status={status}
								isExpire={isExpire}
								ticket={ticket}
								serverTime={serverTime}
								valueFontSize={setFontSize}
							/>
						}
						valueFontSize={setFontSize}
						isCustomContent
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default HistoryList;

const getHistoryCardTitle = (statusLabel, betCount) => {
	const normalizedLabel = String(statusLabel).toLowerCase();
	const safeCount = Number(betCount) || 0;

	if (normalizedLabel === 'denied') {
		return `Denied Bet (${safeCount} bets)`;
	}

	if (normalizedLabel === 'voided' || normalizedLabel === 'voidable') {
		return `Voided Bet (${safeCount} bets)`;
	}

	return `Successful Bet (${safeCount} bets)`;
};

const HistoryRow = ({
	title,
	data,
	color,
	isCustomContent = false,
	isGradientText = false,
	valueFontWeight = '500',
	valueFontSize = '16px',
	valueIcon,
	hideOnMobile = false,
	isMoney = false,
}) => {
	return (
		<Flex
			display={hideOnMobile ? { base: 'none', lg: 'flex' } : 'flex'}
			justifyContent='space-between'
			marginY='8px'
			alignItems='center'
			gap='16px'
			padding={'4px'}
		>
			<Text
				fontSize={valueFontSize}
				fontWeight='500'
				lineHeight='100%'
				color={RECORD_TEXT_COLORS.secondary}
			>
				{title}
			</Text>
			{isCustomContent ? (
				data
			) : isGradientText ? (
				<GradientText
					fontSize={isMoney ? '22px' : valueFontSize}
					fontWeight={valueFontWeight}
					lineHeight='100%'
					textAlign='right'
				>
					{data}
				</GradientText>
			) : (
				<Flex alignItems='center' justifyContent='flex-end' gap='8px' minWidth='0'>
					{valueIcon ? <Box flexShrink={0}>{valueIcon}</Box> : null}
					<Text
						fontSize={isMoney ? '22px' : valueFontSize}
						fontWeight={valueFontWeight}
						lineHeight='100%'
						color={color || RECORD_TEXT_COLORS.primary}
						textAlign='right'
					>
						{data}
					</Text>
				</Flex>
			)}
		</Flex>
	);
};

const HistoryStatusPill = ({ status, isExpire, ticket, serverTime, valueFontSize }) => {
	const styles = getRecordStatusStyle(status, { isExpire, variant: 'history', ticket, serverTime });

	return (
		<Flex
			alignItems='center'
			justifyContent='center'
			minWidth='75px'
			minHeight='27px'
			padding={'4px 12px'}
			borderRadius='100px'
			background={styles.background}
			boxSizing='border-box'
			flexShrink={0}
		>
			<Text fontSize={valueFontSize} fontWeight='600' lineHeight='100%' color={styles.color}>
				{styles.label}
			</Text>
		</Flex>
	);
};
